/**
 * JdDelegatedToRow — the JD page header's "Delegated to" row (wireframe jobs-library-ownership-v3.html; Ownership
 * design, part 2): the delegate's name chip, shown to everyone. The owner takes control back with × and, when
 * nobody is delegated, delegates with + (the picker); the delegate hands control back with × on their own chip.
 * Nobody manages it while editing. Renders nothing when there is no delegate and the viewer can't add one.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { SbTooltip, TooltipTriggerStyled } from 'uiCore';
import { getInitials } from 'Pages/AdminSetup/components/LegalEntityManagement/helper';
import {
  DELEGATED_TO_LABEL, NO_ONE_LABEL, TOOLTIP_DELEGATE_ADD, TOOLTIP_RELINQUISH, YOU_SUFFIX, getDelegatedRow,
  getTakeBackTooltip,
} from 'Utils/jdOwnership';
import {
  ChipAddStyled, ChipRemoveStyled, NoOneStyled, OwnerLabelStyled, OwnerRowStyled, PersonAvatarStyled, PersonChipStyled,
} from './JdCurrentOwnerRow.styles';

const JdDelegatedToRow = ({
  jdOwnership = null, canManage = false, isEditing = false, busy = false, onAdd, onTakeBack, onRelinquish,
}) => {
  const row = getDelegatedRow(jdOwnership, { canManage, isEditing });
  if (!row) return null;
  const { delegate } = row;
  const remove = row.canTakeBack
    ? { tip: getTakeBackTooltip(delegate.name), label: `Take back control from ${delegate.name}`, onClick: onTakeBack }
    : row.canRelinquish && { tip: TOOLTIP_RELINQUISH, label: 'Relinquish control', onClick: onRelinquish };
  return (
    <OwnerRowStyled data-testid="jd-delegated-to">
      <OwnerLabelStyled>{DELEGATED_TO_LABEL}</OwnerLabelStyled>
      {delegate ? (
        <PersonChipStyled>
          <PersonAvatarStyled aria-hidden="true">{getInitials(delegate.name)}</PersonAvatarStyled>
          {delegate.name}
          {delegate.isYou && ` ${YOU_SUFFIX}`}
          {remove && (
            <SbTooltip content={remove.tip}>
              <TooltipTriggerStyled>
                <ChipRemoveStyled type="button" aria-label={remove.label} disabled={busy} onClick={remove.onClick}>
                  ×
                </ChipRemoveStyled>
              </TooltipTriggerStyled>
            </SbTooltip>
          )}
        </PersonChipStyled>
      ) : (
        <NoOneStyled>{NO_ONE_LABEL}</NoOneStyled>
      )}
      {row.canAdd && (
        <SbTooltip content={TOOLTIP_DELEGATE_ADD}>
          <TooltipTriggerStyled>
            <ChipAddStyled type="button" aria-label="Delegate this JD" disabled={busy} onClick={onAdd}>+</ChipAddStyled>
          </TooltipTriggerStyled>
        </SbTooltip>
      )}
    </OwnerRowStyled>
  );
};

JdDelegatedToRow.propTypes = {
  jdOwnership: PropTypes.shape({
    isOwner: PropTypes.bool,
    isDelegate: PropTypes.bool,
    delegateEmail: PropTypes.string,
    delegateName: PropTypes.string,
  }),
  canManage: PropTypes.bool,
  isEditing: PropTypes.bool,
  busy: PropTypes.bool,
  onAdd: PropTypes.func,
  onTakeBack: PropTypes.func,
  onRelinquish: PropTypes.func,
};

export default JdDelegatedToRow;
